import { FC } from 'react';
import { Link } from 'react-router-dom';
import { Box, Center, HStack, Image, VStack } from '@chakra-ui/react';

import FaddedFlower from 'assets/brand/faded-flower.png';
import OkHand from 'assets/brand/ok-hand.png';
import { useCompanyBankAccounts } from 'common/hooks/useCompanyBankAccounts';
import Button from 'components/_core/Button';
import Heading from 'components/_core/Heading';
import SunshineIcon from 'components/_core/SunshineIcon';
import Text from 'components/_core/Text';
import BackButton from 'components/BackButton';
import ErrorView from 'components/ErrorView';
import Loader from 'components/Loader';
import useToast from 'components/ToastProvider/useToast';
import * as Layout from 'layouts/ContentLayout';

import * as routes from '../../routes';
import useDeleteBankAccount from './hooks/useDeleteBankAccount';
import locales from './locales';

interface CloseBankAccountProps {
  match: {
    params: {
      bankAccountId: string;
    };
  };
}

const CloseBankAccount: FC<CloseBankAccountProps> = ({
  match: {
    params: { bankAccountId },
  },
}) => {
  const { showToast } = useToast();

  const { bankAccounts } = useCompanyBankAccounts();
  const currentBankAccount = bankAccounts.find(
    (bankAccount) => bankAccount.bankAccountId === bankAccountId,
  );

  const [deleteAccount, { data, loading }] = useDeleteBankAccount();

  const handleConfirmClick = async () => {
    try {
      await deleteAccount({ variables: { bankAccountId } });
    } catch (error) {
      showToast({ message: locales.deletionError, type: 'error' });
    }
  };

  if (loading) {
    return <Loader />;
  }

  if (!data && !currentBankAccount) {
    return <ErrorView />;
  }

  if (data) {
    return (
      <Layout.Root>
        <Layout.Body>
          <Layout.BodyScrollableSection>
            <Box
              borderColor="grey.300"
              borderRadius="radius-8"
              borderWidth="1px"
              overflow="hidden"
            >
              <Box paddingX="space-32" paddingY="space-40">
                <Center mb="space-24">
                  <Image
                    alt="success"
                    boxSize="180px"
                    fit="cover"
                    role="presentation"
                    src={OkHand}
                  />
                </Center>
                <Heading fontSize="font-26">
                  {locales.formatString(locales.success.description, {
                    name: data.deleteSecondaryBankAccount.name,
                  })}
                </Heading>
              </Box>
              <Box
                borderColor="grey.300"
                borderTopWidth="1px"
                padding="space-24"
              >
                <Center>
                  <Button
                    as={Link}
                    icon="arrow-right"
                    isExpanded
                    textAlign="center"
                    to={routes.bankAccounts}
                    variant="primary"
                  >
                    {locales.seeAllAccounts}
                  </Button>
                </Center>
              </Box>
            </Box>
          </Layout.BodyScrollableSection>
        </Layout.Body>
      </Layout.Root>
    );
  }

  return (
    <Layout.Root>
      <Layout.Body>
        <Box marginBottom="space-16">
          <BackButton />
        </Box>
        <Layout.BodyScrollableSection>
          <Box
            borderColor="grey.300"
            borderRadius="radius-8"
            borderWidth="1px"
            overflow="hidden"
          >
            <Box paddingX="space-32" paddingY="space-40">
              <Center mb="space-24">
                <Image
                  alt="closing"
                  boxSize="180px"
                  fit="cover"
                  role="presentation"
                  src={FaddedFlower}
                />
              </Center>
              <Heading fontSize="font-26">
                {locales.formatString(locales.title, {
                  name: currentBankAccount?.name || '',
                })}
              </Heading>
              {locales.description.map((paragraph, index) => (
                <VStack
                  alignItems="flex-start"
                  key={index}
                  marginTop="space-32"
                  spacing="space-16"
                >
                  <Text fontWeight="weight-500" size="lg">
                    {paragraph.title}
                  </Text>
                  {paragraph.items.map((item, itemIndex) => (
                    <HStack key={itemIndex} spacing="space-16">
                      {index === 1 ? (
                        <SunshineIcon name="tick" />
                      ) : (
                        <Center
                          backgroundColor="grey.300"
                          borderRadius={11}
                          boxSize={20}
                          fontSize="font-13"
                          fontWeight={500}
                        >
                          {itemIndex + 1}
                        </Center>
                      )}
                      <Text>{item}</Text>
                    </HStack>
                  ))}
                </VStack>
              ))}
            </Box>
            <Box borderColor="grey.300" borderTopWidth="1px" padding="space-24">
              <HStack justify="flex-end">
                <Button
                  icon="trash"
                  iconPosition="left"
                  isDisabled={loading}
                  isLoading={loading}
                  onClick={handleConfirmClick}
                  variant="danger"
                >
                  {locales.confirmClosing}
                </Button>
              </HStack>
            </Box>
          </Box>
        </Layout.BodyScrollableSection>
      </Layout.Body>
    </Layout.Root>
  );
};

export default CloseBankAccount;
